/*
 * Renderer probe for production-path bandwidth-governor HIL assertions.
 *
 * Per rule: think-in-systems — compose public LiveKit APIs instead of exposing policy internals.
 * Per rule: code-comments-required — test-only behavior is gated and intentionally narrow.
 */

import type { Device } from '@studio/models/device';
import type { Room } from '@studio/models/room';
import { ConnectionMode } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { broker, locateProxy } from '#~/agent/index.js';
import { broker as lkBroker } from '#~/livekit/broker.js';
import { toHexFromId, toIdFromHex } from '#~/api/index.js';
import type { LiveKit } from '#~/livekit/index.js';
import { locateLiveKit } from '#~/livekit/index.js';

interface BandwidthGovernorProbe {
  videoReceiveDiagnostic: (deviceId: Device['id']) => Promise<VideoReceiveDiagnostic>;
  attachmentDiagnostic: (deviceId: Device['id']) => BandwidthGovernorAttachmentDiagnostic;
  isDeviceAttached: (deviceId: Device['id']) => boolean;
  setManualMode: (deviceId: Device['id'], enabled: boolean) => void;
  setMode: (deviceId: Device['id'], mode: 'cloud' | 'local') => Promise<void>;
  setSelectedVideoDevice: (
    anchorDeviceId: Device['id'],
    selectedDeviceId: Device['id'] | null
  ) => void;
  snapshot: (deviceId: Device['id']) => BandwidthGovernorProbeSnapshot;
}

interface BandwidthGovernorAttachmentDiagnostic {
  agentIdResolved: boolean;
  liveKitFound: boolean;
  proxyFound: boolean;
  room?: ReturnType<LiveKit['getBandwidthAttachmentDiagnostic']>;
}

interface BandwidthGovernorProbeSnapshot {
  deviceState: 'normal' | 'paused-auto' | 'paused-manual' | 'quality-capped';
  encoderConnectionMode?: number;
  governorState: 'congested' | 'disabled' | 'normal';
  manualMode: boolean;
  mode: 'cloud' | 'local';
  roomId: Room['id'];
  track?: {
    kind: string;
    mediaStreamTrackId: string;
    streamState: string;
  };
}

interface EncoderMutator {
  mutate?: (
    mutation: { control: { encoder: { connectionMode: ConnectionMode } } },
    extra: Record<string, never>,
    optimistic: boolean
  ) => Promise<unknown>;
}

interface ProbeContext {
  agentId: Uint8Array;
  liveKit: LiveKit;
  proxy: NonNullable<ReturnType<typeof locateProxy>>;
}

interface ProbeGlobal {
  __irisBandwidthGovernorProbe?: BandwidthGovernorProbe;
}

interface VideoReceiveDiagnostic {
  deviceId: string;
  agentId: string;
  mediaStreamTrackId: string | null;
  subscriptionFailures: Array<{ codec: string; error: 'codec-unsupported' }>;
  receiverCodecs: Array<{ mimeType: string; clockRate: number }>;
  inbound: Array<{
    codec: string | null;
    bytesReceived: number | null;
    packetsReceived: number | null;
    framesDecoded: number | null;
    decoderImplementation: string | null;
  }>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** Allowlisted receive facts keep decoder failures distinct from missing packets. */
function receiveStats(report: RTCStatsReport | undefined): VideoReceiveDiagnostic['inbound'] {
  const inbound: VideoReceiveDiagnostic['inbound'] = [];
  const finite = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;
  report?.forEach((value: unknown) => {
    if (!isRecord(value) || value.type !== 'inbound-rtp' || value.kind !== 'video') return;
    const codec: unknown =
      typeof value.codecId === 'string' ? report.get(value.codecId) : undefined;
    inbound.push({
      codec: isRecord(codec) && typeof codec.mimeType === 'string' ? codec.mimeType : null,
      bytesReceived: finite(value.bytesReceived),
      packetsReceived: finite(value.packetsReceived),
      framesDecoded: finite(value.framesDecoded),
      decoderImplementation:
        typeof value.decoderImplementation === 'string' ? value.decoderImplementation : null
    });
  });
  return inbound;
}

const isEndToEndEnabled = (): boolean => {
  try {
    // Read through the preload bridge (window.electron.process.env), NOT bare
    // `process.env` / `globalThis.process`: the bundler's define replaces
    // cspell:ignore rolldown
    // `process.env` object reads with `{}` at build time (rolldown collapses
    // even `globalThis.process?.env`), so those gates are compile-time false
    // and the probe silently never installs. Same fix as calibration-probe
    // (ENG-4281); the bridge read survives the build verbatim.
    return (
      (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
        .window?.electron?.process?.env?.IRIS_E2E_TEST === '1'
    );
  } catch {
    return false;
  }
};

// Resolve through the same proxy and LiveKit registries used by the UI.
function requireContext(deviceId: Device['id']): ProbeContext {
  const proxy = locateProxy(deviceId);

  if (!proxy) {
    throw new Error(`[bandwidth-governor-probe] No proxy found for device ${deviceId}.`);
  }

  const agentId = toIdFromHex(proxy.id);

  if (!agentId) {
    throw new Error(`[bandwidth-governor-probe] No agent ID found for device ${deviceId}.`);
  }

  const liveKit = locateLiveKit(deviceId);

  if (!liveKit) {
    throw new Error(`[bandwidth-governor-probe] No LiveKit resource found for device ${deviceId}.`);
  }

  return { agentId, liveKit, proxy };
}

// switch.tsx mutates the AGENT proxy from broker, not locateProxy(device).
// HIL locateProxy(device) often has no mutate (same class as missing
// forcePing). 43f35989d9/49623489b4 swallowed that and encoder stayed
// CONNECTION_MODE_LOCAL (cloud_bitrate=0). Per rule: stay-in-scope.
function agentHex(agent: { id?: unknown }): string {
  if (typeof agent.id === 'string') return agent.id.toLowerCase();
  return (toHexFromId(agent.id as Uint8Array) ?? '').toLowerCase();
}

function encoderConnectionModeOf(
  deviceProxy: NonNullable<ReturnType<typeof locateProxy>>
): number | undefined {
  const wanted = String(deviceProxy.id).toLowerCase();
  const fromProxy = (
    deviceProxy as { state?: { control?: { encoder?: { connectionMode?: number } } } }
  ).state?.control?.encoder?.connectionMode;
  if (typeof fromProxy === 'number') return fromProxy;
  for (const agent of broker) {
    if (!agent) continue;
    if (agentHex(agent) !== wanted) continue;
    const mode = (agent as { state?: { control?: { encoder?: { connectionMode?: number } } } })
      .state?.control?.encoder?.connectionMode;
    if (typeof mode === 'number') return mode;
  }
  return undefined;
}

async function mutateEncoderConnectionMode(
  deviceProxy: NonNullable<ReturnType<typeof locateProxy>>,
  mode: 'cloud' | 'local',
  deviceId: Device['id']
): Promise<void> {
  const payload = {
    control: {
      encoder: {
        connectionMode:
          mode === 'cloud'
            ? ConnectionMode.CONNECTION_MODE_CLOUD_AND_LOCAL
            : ConnectionMode.CONNECTION_MODE_LOCAL
      }
    }
  };
  const tryMutate = async (target: EncoderMutator | undefined): Promise<boolean> => {
    if (typeof target?.mutate !== 'function') return false;
    await target.mutate(payload, {}, false);
    return true;
  };
  if (await tryMutate(deviceProxy as EncoderMutator)) return;
  const wanted = String(deviceProxy.id).toLowerCase();
  for (const agent of broker) {
    if (!agent) continue;
    if (agentHex(agent) === wanted && (await tryMutate(agent as EncoderMutator))) {
      return;
    }
  }
  // Own-device match: locateProxy id can differ from broker agent hex on HIL.
  for (const agent of broker) {
    if (!agent) continue;
    const devices = (agent as { state?: { control?: { device?: Record<string, unknown> } } }).state
      ?.control?.device;
    if (devices && deviceId in devices && (await tryMutate(agent as EncoderMutator))) {
      return;
    }
  }
  // Cloud unlock is additive (switch.tsx mutates every agent).
  if (mode === 'cloud') {
    for (const agent of broker) {
      if (!agent) continue;
      if (await tryMutate(agent as EncoderMutator)) return;
    }
  }
}

if (isEndToEndEnabled()) {
  const probe: BandwidthGovernorProbe = {
    async videoReceiveDiagnostic(deviceId): Promise<VideoReceiveDiagnostic> {
      const { liveKit, proxy } = requireContext(deviceId);
      const track = liveKit.videoForAgent(proxy.id, proxy.embedded).get(deviceId);
      // The same public track API supplies room-track-stats.ts. Do not dump a
      // peer, SDP, proxy, or URI: only these receive counters and codec names.
      const report = track === undefined ? undefined : await track.getRTCStatsReport();
      const capabilities =
        typeof RTCRtpReceiver === 'undefined' ? null : RTCRtpReceiver.getCapabilities('video');
      return {
        deviceId,
        agentId: proxy.id,
        mediaStreamTrackId: track?.mediaStreamTrack.id ?? null,
        subscriptionFailures: liveKit.getVideoSubscriptionFailures?.(deviceId) ?? [],
        receiverCodecs:
          capabilities?.codecs.map((codec) => ({
            mimeType: codec.mimeType,
            clockRate: codec.clockRate
          })) ?? [],
        inbound: receiveStats(report)
      };
    },
    attachmentDiagnostic(deviceId): BandwidthGovernorAttachmentDiagnostic {
      const proxy = locateProxy(deviceId);
      const agentId = proxy ? toIdFromHex(proxy.id) : undefined;
      const liveKit = locateLiveKit(deviceId);

      return {
        agentIdResolved: Boolean(agentId),
        liveKitFound: Boolean(liveKit),
        proxyFound: Boolean(proxy),
        ...(liveKit ? { room: liveKit.getBandwidthAttachmentDiagnostic() } : {})
      };
    },

    // Non-throwing readiness lets HIL wait while the proxy and room attach independently.
    isDeviceAttached(deviceId): boolean {
      const proxy = locateProxy(deviceId);
      if (!proxy) return false;
      if (!toIdFromHex(proxy.id)) return false;
      return Boolean(locateLiveKit(deviceId));
    },

    snapshot(deviceId): BandwidthGovernorProbeSnapshot {
      const { agentId, liveKit, proxy } = requireContext(deviceId);
      const track = liveKit.videoForAgent(proxy.id, proxy.embedded).get(deviceId);
      const encoderConnectionMode = encoderConnectionModeOf(proxy);

      return {
        deviceState: liveKit.getBandwidthDeviceState(deviceId),
        ...(typeof encoderConnectionMode === 'number' ? { encoderConnectionMode } : {}),
        governorState: liveKit.getBandwidthGovernorState(),
        manualMode: liveKit.isManualLowBandwidthMode(),
        mode: liveKit.modeForAgent(agentId),
        roomId: liveKit.id,
        ...(track
          ? {
              track: {
                // kind is the discriminator for the audio-in-video-bucket
                // class (CI #1509 run 32904567960: the failure dump held an
                // audio sid in the video slot for 60s and nothing named it).
                kind: String(track.kind),
                mediaStreamTrackId: track.mediaStreamTrack.id,
                streamState: String(track.streamState)
              }
            }
          : {})
      };
    },

    async setMode(deviceId, mode): Promise<void> {
      const { agentId, liveKit, proxy } = requireContext(deviceId);
      // Match switch.tsx: session allow/deny fans out before setMode.
      const policy = mode === 'cloud' ? 'allow' : 'deny';
      for (const lk of lkBroker) {
        lk.setCloudVideoPolicy(policy);
      }
      try {
        await mutateEncoderConnectionMode(proxy, mode, deviceId);
      } catch {
        // Incomplete HIL proxies must not abort setModeAndWait.
      }
      await liveKit.setMode(mode, agentId);
      await liveKit.refreshTrackSubscriptions(agentId);
      await liveKit.forceVideoReattach();
      for (const agent of broker) {
        agent?.forcePing?.();
      }
    },

    setSelectedVideoDevice(anchorDeviceId, selectedDeviceId): void {
      requireContext(anchorDeviceId).liveKit.setSelectedVideoDevice(selectedDeviceId);
    },

    setManualMode(deviceId, enabled): void {
      requireContext(deviceId).liveKit.setManualLowBandwidthMode(enabled);
    }
  };

  (<ProbeGlobal & typeof globalThis>globalThis).__irisBandwidthGovernorProbe = probe;
}
